import { HttpRequest, HttpResponse, HttpEvent } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { delay } from 'rxjs/operators';
import { PMDF_UNIDADES_RAW } from './organizacao.fixtures';
import {
  Unidade, TipoUnidade, NaturezaUnidade, TipoChefia, CategoriaUnidade, TipoSubordinacao,
  PrevistoUnidade, PrevistoItem, CodigoQuadro, Patente, TipoPessoa, PatenteLabel, Funcao, StatusSimulacao,
  Quadro, TipoQuadro
} from '../models/organizacao.model';

// Local Quadros list for in-memory session persistence
let localQuadros: Quadro[] | null = null;

function getLocalQuadros(): Quadro[] {
  if (!localQuadros) {
    localQuadros = [
      { codigo: CodigoQuadro.QOPM, nome: 'Quadro de Oficiais Policiais Militares Combatentes', tipo: TipoQuadro.OFICIAL, emExtincao: false, efetivoFixadoLei: { [Patente.CEL]: 30, [Patente.TC]: 80, [Patente.MAJ]: 150, [Patente.CAP]: 300, [Patente['1TEN']]: 400, [Patente['2TEN']]: 200 } },
      { codigo: CodigoQuadro.QOPMSM, nome: 'Quadro de Oficiais Policiais Militares Médicos', tipo: TipoQuadro.OFICIAL, emExtincao: false, efetivoFixadoLei: { [Patente.TC]: 15, [Patente.MAJ]: 25, [Patente.CAP]: 30, [Patente['1TEN']]: 35 } },
      { codigo: CodigoQuadro.QOPMM, nome: 'Quadro de Oficiais Policiais Militares Músicos', tipo: TipoQuadro.OFICIAL, emExtincao: false, efetivoFixadoLei: { [Patente.MAJ]: 1, [Patente.CAP]: 1, [Patente['1TEN']]: 2, [Patente['2TEN']]: 2 } },
      { codigo: CodigoQuadro.QOPMA, nome: 'Quadro de Oficiais Policiais Militares de Administração', tipo: TipoQuadro.OFICIAL, emExtincao: false, efetivoFixadoLei: { [Patente.CAP]: 20, [Patente['1TEN']]: 50, [Patente['2TEN']]: 40 } },
      { codigo: CodigoQuadro.QPPMC, nome: 'Quadro de Praças Policiais Militares Combatentes', tipo: TipoQuadro.PRACA, emExtincao: false, efetivoFixadoLei: { [Patente.ST]: 500, [Patente['1SGT']]: 1000, [Patente['2SGT']]: 2000, [Patente['3SGT']]: 3000, [Patente.CB]: 4000, [Patente.SD]: 6000 } },
      { codigo: CodigoQuadro.QPMP_1, nome: 'QPMP-1 - Auxiliares de Saúde', tipo: TipoQuadro.PRACA, emExtincao: false, efetivoFixadoLei: { [Patente.ST]: 15, [Patente['1SGT']]: 30, [Patente['2SGT']]: 50, [Patente['3SGT']]: 80, [Patente.CB]: 120, [Patente.SD]: 150 } },
      { codigo: CodigoQuadro.QPMP_4, nome: 'QPMP-4 - Praças Músicos', tipo: TipoQuadro.PRACA, emExtincao: false, efetivoFixadoLei: { [Patente.ST]: 12, [Patente['1SGT']]: 25, [Patente['2SGT']]: 40, [Patente['3SGT']]: 60, [Patente.CB]: 90, [Patente.SD]: 120 } },
      { codigo: CodigoQuadro.QPMP_9, nome: 'QPMP-9 - Praças Artífices', tipo: TipoQuadro.PRACA, emExtincao: true, efetivoFixadoLei: { [Patente.ST]: 5, [Patente['1SGT']]: 10, [Patente['2SGT']]: 15, [Patente['3SGT']]: 20, [Patente.CB]: 30, [Patente.SD]: 50 } }
    ];
  }
  return localQuadros;
}

// Helper methods from original service
function mapUpmToUnidade(node: any, parentId: string | null = null): Unidade {
  const id = node.codigo.toString();
  const sigla = node.sigla;
  const nome = node.nome;
  const ativo = node.ativo ?? true;
  const codigoQo = node.nrLocalizacao || `QO-${id}`;
  const categoria = CategoriaUnidade.QODE_MACRO;
  let natureza: NaturezaUnidade = NaturezaUnidade.APOIO;
  if (node.tipoNome === 'OPERACIONAL' || node.tipoSigla === 'OPER') {
    natureza = NaturezaUnidade.OPERACIONAL;
  }
  let tipo: TipoUnidade = TipoUnidade.OUTRO;
  let tipoChefia: TipoChefia = TipoChefia.CHEFIA;
  const siglaUpper = sigla.toUpperCase();
  const nomeUpper = nome.toUpperCase();

  if (siglaUpper.includes('CMT GERAL') || nomeUpper.includes('COMANDO GERAL')) {
    tipo = TipoUnidade.COMANDO_GERAL;
    tipoChefia = TipoChefia.COMANDO_GERAL;
  } else if (siglaUpper === 'SUBCMTGERAL' || nomeUpper.includes('SUBCOMANDANTE-GERAL')) {
    tipo = TipoUnidade.OUTRO;
    tipoChefia = TipoChefia.COMANDO;
  } else if (siglaUpper.match(/^D[A-Z]{2}$/) && nomeUpper.includes('DEPARTAMENTO')) {
    tipo = TipoUnidade.DEPARTAMENTO;
    tipoChefia = TipoChefia.CHEFIA;
  } else if (
    nomeUpper.includes('DIRETORIA') ||
    siglaUpper.startsWith('DIR') ||
    ['DEA', 'DEEC', 'DIPC', 'DPP', 'DPM', 'DVPC', 'DINFRA', 'DALF', 'DPMT', 'DPTS', 'DITEL', 'DAO', 'DAS', 'DEOF', 'DPGC', 'DIFORM'].includes(siglaUpper)
  ) {
    tipo = TipoUnidade.DIRETORIA;
    tipoChefia = TipoChefia.DIRECAO;
  } else if (
    nomeUpper.includes('CENTRO') ||
    siglaUpper.startsWith('CENTRO') ||
    ['CCS', 'CI', 'CETI', 'CAPS', 'CASO', 'CMED', 'CCF', 'CPSO', 'COPOM', 'CPSP', 'CPQV', 'CAO', 'CO', 'CMBEL', 'CMAN', 'CMEDVET'].includes(siglaUpper)
  ) {
    tipo = TipoUnidade.CENTRO;
    tipoChefia = TipoChefia.CHEFIA;
  } else if (
    nomeUpper.includes('COMANDO') ||
    siglaUpper.includes('CPR') ||
    ['CPAM', 'CPESC', 'CPESP', 'CPRL', 'CPRM', 'CPRO', 'CPRS', 'CPME', 'CPTRAN'].includes(siglaUpper)
  ) {
    tipo = TipoUnidade.COMANDO_POLICIAMENTO;
    tipoChefia = TipoChefia.COMANDO;
  } else if (siglaUpper.includes('BPM') || nomeUpper.includes('BATALHÃO') || ['ROTAM', 'BOPE'].includes(siglaUpper)) {
    tipo = TipoUnidade.BATALHAO;
    tipoChefia = TipoChefia.COMANDO;
  } else if (siglaUpper.includes('RPMON') || nomeUpper.includes('REGIMENTO')) {
    tipo = TipoUnidade.REGIMENTO;
    tipoChefia = TipoChefia.COMANDO;
  } else if (siglaUpper.includes('CIA') || nomeUpper.includes('COMPANHIA')) {
    tipo = TipoUnidade.COMPANHIA;
    tipoChefia = TipoChefia.COMANDO;
  } else if (siglaUpper.includes('ESQ') || nomeUpper.includes('ESQUADRÃO')) {
    tipo = TipoUnidade.ESQUADRAO;
    tipoChefia = TipoChefia.COMANDO;
  } else if (siglaUpper.includes('DIV') || nomeUpper.includes('DIVISÃO')) {
    tipo = TipoUnidade.DIVISAO;
    tipoChefia = TipoChefia.CHEFIA;
  } else if (siglaUpper.includes('SEC') || nomeUpper.includes('SEÇÃO') || ['AUD', 'SAV'].includes(siglaUpper)) {
    tipo = TipoUnidade.SECAO;
    tipoChefia = TipoChefia.CHEFIA;
  } else if (siglaUpper.includes('SUBSEC') || nomeUpper.includes('SUBSEÇÃO')) {
    tipo = TipoUnidade.SUBSECAO;
    tipoChefia = TipoChefia.CHEFIA;
  } else if (siglaUpper.includes('GAB') || nomeUpper.includes('GABINETE') || siglaUpper === 'GCG') {
    tipo = TipoUnidade.GABINETE;
    tipoChefia = TipoChefia.CHEFIA;
  } else if (siglaUpper.includes('ASS') || nomeUpper.includes('ASSESSORIA') || siglaUpper === 'SRI') {
    tipo = TipoUnidade.ASSESSORIA;
    tipoChefia = TipoChefia.CHEFIA;
  }

  const filhos: Unidade[] = [];
  if (node.subUnidades && Array.isArray(node.subUnidades)) {
    for (const sub of node.subUnidades) {
      filhos.push(mapUpmToUnidade(sub, id));
    }
  }

  return {
    id, codigoQo, sigla, nome, unidadePaiId: parentId, tipo, categoria, natureza,
    tipoSubordinacao: parentId ? TipoSubordinacao.HIERARQUICA : null,
    tipoChefia, ativo, dataInicioVigencia: '2026-01-01', dataFimVigencia: null,
    normaReferencia: 'Portaria PMDF 2026', telefones: ['(61) 3190-0000'],
    filhos, totalSubunidades: filhos.length, totalSecoes: 0
  };
}

function filterUnidadeTree(unidades: Unidade[], filtros: any): Unidade[] {
  return unidades
    .map(u => {
      const filteredFilhos = u.filhos ? filterUnidadeTree(u.filhos, filtros) : [];
      return { ...u, filhos: filteredFilhos, totalSubunidades: filteredFilhos.length };
    })
    .filter(u => {
      let matches = true;
      if (filtros.ativo !== undefined && u.ativo !== filtros.ativo) matches = false;
      if (filtros.tipo && u.tipo !== filtros.tipo) matches = false;
      if (filtros.natureza && u.natureza !== filtros.natureza) matches = false;
      if (filtros.busca) {
        const b = filtros.busca.toLowerCase();
        const siglaMatch = u.sigla.toLowerCase().includes(b);
        const nomeMatch = u.nome.toLowerCase().includes(b);
        const codeMatch = u.codigoQo.toLowerCase().includes(b);
        if (!siglaMatch && !nomeMatch && !codeMatch) matches = false;
      }
      return matches || (u.filhos && u.filhos.length > 0);
    });
}

function findUnidadeById(unidades: Unidade[], id: string): Unidade | null {
  for (const u of unidades) {
    if (u.id === id) return u;
    if (u.filhos && u.filhos.length > 0) {
      const found = findUnidadeById(u.filhos, id);
      if (found) return found;
    }
  }
  return null;
}

function generatePrevistoForUnidade(unidade: Unidade): PrevistoUnidade {
  const items: PrevistoItem[] = [];
  if (unidade.tipo === TipoUnidade.COMANDO_GERAL) {
    items.push(
      { quadro: CodigoQuadro.QOPM, patente: Patente.CEL, tipoPessoa: TipoPessoa.PM, quantidadePrevista: 2 },
      { quadro: CodigoQuadro.QOPM, patente: Patente.TC, tipoPessoa: TipoPessoa.PM, quantidadePrevista: 4 },
      { quadro: CodigoQuadro.QOPM, patente: Patente.MAJ, tipoPessoa: TipoPessoa.PM, quantidadePrevista: 6 },
      { quadro: CodigoQuadro.QOPM, patente: Patente.CAP, tipoPessoa: TipoPessoa.PM, quantidadePrevista: 10 },
      { quadro: CodigoQuadro.QPPMC, patente: Patente.ST, tipoPessoa: TipoPessoa.PM, quantidadePrevista: 5 },
      { quadro: CodigoQuadro.QPPMC, patente: Patente.CB, tipoPessoa: TipoPessoa.PM, quantidadePrevista: 20 },
      { quadro: CodigoQuadro.QPPMC, patente: Patente.SD, tipoPessoa: TipoPessoa.PM, quantidadePrevista: 30 }
    );
  } else if (unidade.tipo === TipoUnidade.BATALHAO) {
    items.push(
      { quadro: CodigoQuadro.QOPM, patente: Patente.TC, tipoPessoa: TipoPessoa.PM, quantidadePrevista: 1 },
      { quadro: CodigoQuadro.QOPM, patente: Patente.MAJ, tipoPessoa: TipoPessoa.PM, quantidadePrevista: 3 },
      { quadro: CodigoQuadro.QOPM, patente: Patente.CAP, tipoPessoa: TipoPessoa.PM, quantidadePrevista: 6 },
      { quadro: CodigoQuadro.QOPM, patente: Patente['1TEN'], tipoPessoa: TipoPessoa.PM, quantidadePrevista: 8 },
      { quadro: CodigoQuadro.QPPMC, patente: Patente['1SGT'], tipoPessoa: TipoPessoa.PM, quantidadePrevista: 12 },
      { quadro: CodigoQuadro.QPPMC, patente: Patente['2SGT'], tipoPessoa: TipoPessoa.PM, quantidadePrevista: 20 },
      { quadro: CodigoQuadro.QPPMC, patente: Patente.CB, tipoPessoa: TipoPessoa.PM, quantidadePrevista: 50 },
      { quadro: CodigoQuadro.QPPMC, patente: Patente.SD, tipoPessoa: TipoPessoa.PM, quantidadePrevista: 100 }
    );
  } else {
    items.push(
      { quadro: CodigoQuadro.QOPM, patente: Patente.CAP, tipoPessoa: TipoPessoa.PM, quantidadePrevista: 1 },
      { quadro: CodigoQuadro.QPPMC, patente: Patente.CB, tipoPessoa: TipoPessoa.PM, quantidadePrevista: 4 },
      { quadro: CodigoQuadro.QPPMC, patente: Patente.SD, tipoPessoa: TipoPessoa.PM, quantidadePrevista: 6 }
    );
  }
  const totalPrevisto = items.reduce((sum, item) => sum + item.quantidadePrevista, 0);
  return { unidadeId: unidade.id, nomeUnidade: unidade.nome, totalPrevisto, itens: items };
}

function generateEstruturaInternaForUnidade(unidade: Unidade): Unidade[] {
  const secoes: Unidade[] = [];
  const rootId = `${unidade.id}-cmt`;
  secoes.push({
    id: rootId, codigoQo: `${unidade.codigoQo}.1`, sigla: 'CMT', nome: 'Comando do Batalhão',
    unidadePaiId: unidade.id, tipo: TipoUnidade.CHEFIA_INTERNA, categoria: CategoriaUnidade.ESTRUTURA_INTERNA,
    ativo: true, tipoChefia: TipoChefia.COMANDO, filhos: []
  });
  if (unidade.tipo === TipoUnidade.BATALHAO) {
    secoes.push(
      { id: `${unidade.id}-sec1`, codigoQo: `${unidade.codigoQo}.2`, sigla: 'SEC-ADM', nome: 'Seção Administrativa', unidadePaiId: rootId, tipo: TipoUnidade.SECAO, categoria: CategoriaUnidade.ESTRUTURA_INTERNA, ativo: true, filhos: [] },
      { id: `${unidade.id}-sec2`, codigoQo: `${unidade.codigoQo}.3`, sigla: 'SEC-OPER', nome: 'Seção Operacional', unidadePaiId: rootId, tipo: TipoUnidade.SECAO, categoria: CategoriaUnidade.ESTRUTURA_INTERNA, ativo: true, filhos: [] }
    );
  } else {
    secoes.push({ id: `${unidade.id}-sec`, codigoQo: `${unidade.codigoQo}.2`, sigla: 'SEC-GEN', nome: 'Seção Geral', unidadePaiId: rootId, tipo: TipoUnidade.SECAO, categoria: CategoriaUnidade.ESTRUTURA_INTERNA, ativo: true, filhos: [] });
  }
  return secoes;
}

function generateFuncoesForUnidade(unidade: Unidade): Funcao[] {
  const funcoes: Funcao[] = [];
  const previsto = generatePrevistoForUnidade(unidade);
  previsto.itens.forEach((item, index) => {
    funcoes.push({
      id: `${unidade.id}-func-${index}`, unidadeId: unidade.id,
      descricao: `Função de ${item.patente ? PatenteLabel[item.patente] : 'Militar'} - ${item.quadro}`,
      quadro: item.quadro, patente: item.patente, quantidadePrevista: item.quantidadePrevista,
      tipoPessoa: item.tipoPessoa, gratificada: index === 0,
      simboloCargo: index === 0 ? (unidade.tipo === TipoUnidade.COMANDO_GERAL ? 'CGE' : 'FC-1') : null,
      acumuladaPorFuncaoId: null, observacao: 'Função gerada a partir dos dados estruturais da PMDF.',
      dataInicioVigencia: '2026-01-01'
    });
  });
  return funcoes;
}

const TREE = filterUnidadeTree(PMDF_UNIDADES_RAW.map(node => mapUpmToUnidade(node)), {});

export function handleOrganizacaoRequest(req: HttpRequest<any>, delayMs: number): Observable<HttpEvent<any>> {
  const url = req.url;
  
  if (url.includes('estado-maior.rs.pm.df.gov.br/upm')) {
    return of(new HttpResponse({ status: 200, body: PMDF_UNIDADES_RAW })).pipe(delay(delayMs));
  }

  if (url.includes('/unidades') && !url.includes('/estrutura-interna') && !url.includes('/previsto') && !url.includes('/subordinadas') && !url.includes('/funcoes')) {
    const parts = url.split('/');
    const last = parts[parts.length - 1];
    if (last === 'unidades' || last.includes('?')) {
      return of(new HttpResponse({ status: 200, body: TREE })).pipe(delay(delayMs));
    } else {
      const id = last.split('?')[0];
      const found = findUnidadeById(TREE, id);
      return of(new HttpResponse({ status: found ? 200 : 404, body: found })).pipe(delay(delayMs));
    }
  }

  if (url.includes('/subordinadas')) {
    const parts = url.split('/');
    const id = parts[parts.indexOf('unidades') + 1];
    const found = findUnidadeById(TREE, id);
    const subordinadas = found?.filhos?.map(f => f.id) || [];
    return of(new HttpResponse({ status: 200, body: { unidadeId: id, subordinadas } })).pipe(delay(delayMs));
  }

  if (url.includes('/previsto')) {
    const parts = url.split('/');
    const id = parts[parts.indexOf('unidades') + 1];
    const found = findUnidadeById(TREE, id);
    if (found) {
      return of(new HttpResponse({ status: 200, body: generatePrevistoForUnidade(found) })).pipe(delay(delayMs));
    }
  }

  if (url.includes('/estrutura-interna')) {
    const parts = url.split('/');
    const id = parts[parts.indexOf('unidades') + 1];
    const found = findUnidadeById(TREE, id);
    if (found) {
      return of(new HttpResponse({ status: 200, body: generateEstruturaInternaForUnidade(found) })).pipe(delay(delayMs));
    }
  }

  if (url.includes('/funcoes/gratificadas')) {
    const gratificadas: any[] = [];
    const recurse = (unidades: Unidade[]) => {
      unidades.forEach(u => {
        const funcs = generateFuncoesForUnidade(u);
        funcs.forEach(f => {
          if (f.gratificada) {
            gratificadas.push({
              id: f.id, unidadeId: u.id, nomeUnidade: u.nome, descricao: f.descricao,
              simboloCargo: f.simboloCargo || 'FC', quadro: f.quadro, patente: f.patente, quantidadePrevista: f.quantidadePrevista
            });
          }
        });
        if (u.filhos) recurse(u.filhos);
      });
    };
    recurse(TREE);
    return of(new HttpResponse({ status: 200, body: gratificadas })).pipe(delay(delayMs));
  }

  if (url.includes('/funcoes') && url.includes('unidades/')) {
    const parts = url.split('/');
    const id = parts[parts.indexOf('unidades') + 1];
    const found = findUnidadeById(TREE, id);
    if (found) {
      return of(new HttpResponse({ status: 200, body: generateFuncoesForUnidade(found) })).pipe(delay(delayMs));
    }
  }

  if (url.includes('/funcoes')) {
    const todas: Funcao[] = [];
    const recurse = (unidades: Unidade[]) => {
      unidades.forEach(u => {
        todas.push(...generateFuncoesForUnidade(u));
        if (u.filhos) recurse(u.filhos);
      });
    };
    recurse(TREE);
    return of(new HttpResponse({ status: 200, body: todas })).pipe(delay(delayMs));
  }

  // QODE & Simulações
  if (url.includes('/qode/historico')) {
    return of(new HttpResponse({ status: 200, body: [
      { id: 'qode-2025', numeroQo: 'QO-2025', numeroPortaria: 'Portaria PMDF 456/2025', dataPortaria: '2025-06-15', dataVigencia: '2025-07-01', publicadoPor: 'Estado-Maior PMDF', publicadoEm: '2025-06-15T14:30:00Z' },
      { id: 'qode-vigente', numeroQo: 'QO-VIGENTE-PMDF', numeroPortaria: 'Portaria PMDF 2026', dataPortaria: '2026-01-01', dataVigencia: '2026-01-01', publicadoPor: 'Estado-Maior PMDF', publicadoEm: '2026-01-01T12:00:00Z' }
    ] })).pipe(delay(delayMs));
  }

  if (url.includes('/qode')) {
    const is2025 = url.includes('qode-2025');
    return of(new HttpResponse({ status: 200, body: {
      id: is2025 ? 'qode-2025' : 'qode-vigente', numeroQo: is2025 ? 'QO-2025' : 'QO-VIGENTE-PMDF',
      numeroPortaria: is2025 ? 'Portaria PMDF 456/2025' : 'Portaria PMDF 2026',
      dataPortaria: is2025 ? '2025-06-15' : '2026-01-01', dataVigencia: is2025 ? '2025-07-01' : '2026-01-01',
      publicadoPor: 'Estado-Maior PMDF', publicadoEm: is2025 ? '2025-06-15T14:30:00Z' : '2026-01-01T12:00:00Z',
      snapshotJson: PMDF_UNIDADES_RAW
    } })).pipe(delay(delayMs));
  }

  if (url.includes('/simulacoes') && !url.includes('impacto') && !url.includes('comparativo')) {
    const parts = url.split('/');
    const last = parts[parts.length - 1];
    if (last === 'simulacoes' || last.includes('?')) {
      return of(new HttpResponse({ status: 200, body: [
        { id: 'sim-1', nome: 'Reorganização CPRO / 2º BPM', descricao: 'Estudo para transferência de subordinação do 2º BPM.', status: StatusSimulacao.RASCUNHO, criadoPor: 'MAJ QOPM Silva', criadoEm: '2026-05-22T10:00:00Z', baseadoEmQodeId: 'qode-vigente' }
      ] })).pipe(delay(delayMs));
    } else {
      return of(new HttpResponse({ status: 200, body: {
        id: last, nome: 'Reorganização CPRO / 2º BPM', descricao: 'Estudo para transferência de subordinação do 2º BPM.',
        status: StatusSimulacao.RASCUNHO, criadoPor: 'MAJ QOPM Silva', criadoEm: '2026-05-22T10:00:00Z',
        baseadoEmQodeId: 'qode-vigente', workspaceJson: PMDF_UNIDADES_RAW
      } })).pipe(delay(delayMs));
    }
  }

  if (url.includes('/comparativo')) {
    const id = url.split('/')[url.split('/').indexOf('simulacoes') + 1];
    return of(new HttpResponse({ status: 200, body: {
      simulacaoId: id,
      unidadesCriadas: [{ id: '10', codigoQo: 'QO-10', sigla: 'BOPE', nome: 'Batalhão de Operações Especiais', tipo: TipoUnidade.BATALHAO, categoria: CategoriaUnidade.QODE_MACRO, ativo: true }],
      unidadesExtintas: [{ id: '7', codigoQo: 'QO-7', sigla: 'DEC', nome: 'Departamento de Educação e Cultura', tipo: TipoUnidade.DEPARTAMENTO, categoria: CategoriaUnidade.QODE_MACRO, ativo: true }],
      unidadesReorganizadas: [{ unidade: { id: '3', codigoQo: 'QO-3', sigla: '9º BPM', nome: '9º Batalhão de Polícia Militar', tipo: TipoUnidade.BATALHAO, categoria: CategoriaUnidade.QODE_MACRO, ativo: true }, alteracoes: [{ campo: 'Nome', valorAnterior: '9º Batalhão de Polícia Militar', valorNovo: '9º Batalhão de Polícia Militar (Reestruturado)' }, { campo: 'Unidade Pai', valorAnterior: 'Comando Geral', valorNovo: '1º CPRM' }, { campo: 'Efetivo Previsto', valorAnterior: '196', valorNovo: '216 (+20 vagas)' }] }],
      variacaoPrevisto: [{ quadro: CodigoQuadro.QOPM, patente: Patente.CEL, previstoAtual: 30, previstoNovo: 32, delta: 2 }, { quadro: CodigoQuadro.QOPM, patente: Patente.TC, previstoAtual: 80, previstoNovo: 81, delta: 1 }, { quadro: CodigoQuadro.QOPM, patente: Patente.MAJ, previstoAtual: 150, previstoNovo: 152, delta: 2 }, { quadro: CodigoQuadro.QOPM, patente: Patente.CAP, previstoAtual: 300, previstoNovo: 310, delta: 10 }, { quadro: CodigoQuadro.QPPMC, patente: Patente.CB, previstoAtual: 4000, previstoNovo: 4015, delta: 15 }, { quadro: CodigoQuadro.QPPMC, patente: Patente.SD, previstoAtual: 6000, previstoNovo: 5950, delta: -50 }]
    } })).pipe(delay(delayMs));
  }

  if (url.includes('/impacto')) {
    const id = url.split('/')[url.split('/').indexOf('simulacoes') + 1];
    return of(new HttpResponse({ status: 200, body: {
      simulacaoId: id, totalUnidadesAfetadas: 3, unidadesCriadas: 1, unidadesExtintas: 1, unidadesReorganizadas: 1,
      variacaoEfetivoPorPatente: [{ quadro: CodigoQuadro.QOPM, patente: Patente.CEL, delta: 2 }, { quadro: CodigoQuadro.QOPM, patente: Patente.TC, delta: 1 }, { quadro: CodigoQuadro.QOPM, patente: Patente.MAJ, delta: 2 }, { quadro: CodigoQuadro.QOPM, patente: Patente.CAP, delta: 10 }, { quadro: CodigoQuadro.QPPMC, patente: Patente.CB, delta: 15 }, { quadro: CodigoQuadro.QPPMC, patente: Patente.SD, delta: -50 }],
      funcoesGratificadasImpactadas: 4, alertaTeto: false, alertaTetoDetalhe: null
    } })).pipe(delay(delayMs));
  }

  if (url.includes('/quadros')) {
    const parts = url.split('/');
    const last = parts[parts.length - 1].split('?')[0];
    const currentQuadros = getLocalQuadros();

    if (req.method === 'GET') {
      if (last === 'quadros') {
        return of(new HttpResponse({ status: 200, body: currentQuadros })).pipe(delay(delayMs));
      } else {
        const found = currentQuadros.find(q => q.codigo === last);
        return of(new HttpResponse({ status: found ? 200 : 404, body: found })).pipe(delay(delayMs));
      }
    }

    if (req.method === 'PUT') {
      const idx = currentQuadros.findIndex(q => q.codigo === last);
      if (idx !== -1) {
        const updated = { ...currentQuadros[idx], ...req.body };
        currentQuadros[idx] = updated;
        return of(new HttpResponse({ status: 200, body: updated })).pipe(delay(delayMs));
      }
      return of(new HttpResponse({ status: 404, body: { detail: 'Quadro não encontrado' } })).pipe(delay(delayMs));
    }

    if (req.method === 'POST') {
      const novo = req.body as Quadro;
      currentQuadros.push(novo);
      return of(new HttpResponse({ status: 201, body: novo })).pipe(delay(delayMs));
    }
  }

  if (url.includes('/relatorios/atividade')) {
    let totalGeralPrevisto = 0;
    const apoios: any[] = [];
    const operacionais: any[] = [];
    const recurse = (unidades: Unidade[]) => {
      unidades.forEach(u => {
        const previsto = generatePrevistoForUnidade(u);
        totalGeralPrevisto += previsto.totalPrevisto;
        const item = { id: u.id, sigla: u.sigla, nome: u.nome, previsto: previsto.totalPrevisto };
        if (u.natureza === NaturezaUnidade.OPERACIONAL) operacionais.push(item);
        else apoios.push(item);
        if (u.filhos) recurse(u.filhos);
      });
    };
    recurse(TREE);
    const totalApoio = apoios.reduce((sum, item) => sum + item.previsto, 0);
    const totalOperacional = operacionais.reduce((sum, item) => sum + item.previsto, 0);

    return of(new HttpResponse({ status: 200, body: {
      dataReferencia: null, totalGeralPrevisto,
      grupos: [
        { natureza: NaturezaUnidade.APOIO, totalPrevisto: totalApoio, percentual: totalGeralPrevisto > 0 ? (totalApoio / totalGeralPrevisto) * 100 : 0, itensPorQuadroPatente: [], unidades: apoios },
        { natureza: NaturezaUnidade.OPERACIONAL, totalPrevisto: totalOperacional, percentual: totalGeralPrevisto > 0 ? (totalOperacional / totalGeralPrevisto) * 100 : 0, itensPorQuadroPatente: [], unidades: operacionais }
      ]
    } })).pipe(delay(delayMs));
  }

  return of(new HttpResponse({ status: 200, body: {} })).pipe(delay(delayMs));
}
